/**
 * Dev-only mock backend (VITE_MOCK=1). Serves a small fake world through the same
 * request shape as the real API so every screen renders without a server.
 * All objects are typed against @pk/shared so contract drift is caught by tsc.
 *
 * Query param ?mock=new -> account without a house (found-house flow)
 *             ?mock=ended -> account whose last house ended
 */
import {
  BASE_PRICES, CITY_TERM_DAYS, COUNCIL_SEATS, COMPANY_WAGES, DEFAULT_STANDING_ORDERS, GUILD_XP_GUILDMASTER, GUILD_XP_JOURNEYMAN, GUILD_XP_MASTER,
  SQUIRE_XP_MAX, SQUIRE_XP_SEASONED, SQUIRE_XP_SENIOR,
  MAKER_STOCK_BASE, MAKER_TICK, MARKET_ORDER_EXPIRY_DAYS, MAX_MARKET_ORDER_QTY, MAX_TARIFF_RATE, MS_PER_GAME_DAY, MS_PER_GAME_HOUR,
  START_STATE, TRADABLE_RESOURCES, CARAVAN_DAYS_PER_ROAD_DAY, CARAVAN_UNREACHABLE_DAYS, absHourOf, childFaceFromSeed, dayToDate, encodeCrest, faceFromSeed,
} from '@pk/shared';
import type {
  ArmyView, AttributeKey, FoundHouseRequest, Attributes, CharacterHistory, ChronicleEntry, ChronicleKind, Command, CommandResult,
  ChatChannelKind, ChatChannelSummary, ChatChannelsResponse, ChatMessage, ChatMessagesResponse, DmTarget,
  CouncilSeat, CharacterSummary, CharacterView, HistoryCharacter, House, HouseSummary, HouseView, Id, Letter,
  KnightTier, MarriageCandidate, ServiceCandidate, SquireCandidate, SquireRank, MeResponse, Proposal, QueuedCommand, RealmLaws, Road, Terrain, TitleRank, TitleSummary, TitleTenureView, TitleView,
  Troops, WarView, WorldView, CitiesView, CityElectionView, CompanyView, DeliveriesView, DeliveryRow, GuildView, LoansView, MarketQuote, MarketView, MyMarketOrderView, TradableResource,
} from '@pk/shared';

const NOW_DAY = 360 * 150 + 100;
const PLAYER_HOUSE = 'h1';
const MY_ACCOUNT = 'acc1';

let mode: 'normal' | 'new' | 'ended' = 'normal';
try {
  const m = new URLSearchParams(location.search).get('mock');
  if (m === 'new' || m === 'ended') mode = m;
} catch { /* not in browser */ }
let founded = mode === 'normal';
let endedDismissed = false;

// ------------------------------------------------------------------ helpers

function hash(n: number): number {
  let x = (n * 2654435761) >>> 0;
  x ^= x >>> 15; x = Math.imul(x, 2246822519) >>> 0; x ^= x >>> 13;
  return (x >>> 0) / 4294967296;
}
function genes(seed: number): number[] {
  return Array.from({ length: 12 }, (_, i) => hash(seed * 31 + i * 7 + 3));
}
/** Faces chosen in the house designer, by character id. */
const faceOverride: Record<Id, number[]> = {};
/** Skill breakdown for own-house characters (aptitude = half the value, XP part-way to the next rank). */
function mockSkills(a: Attributes, own: boolean, age: number, seed: number): Pick<CharacterView, 'skills' | 'focus' | 'focusActive' | 'focusReadyDay'> {
  if (!own) return { skills: null, focus: null, focusActive: null, focusReadyDay: null };
  const keys: AttributeKey[] = ['martial', 'diplomacy', 'stewardship', 'intrigue', 'learning', 'prowess'];
  const skills = keys.map((key, i) => {
    const aptitude = Math.round(a[key] / 2);
    const rank = Math.min(10, a[key] - aptitude);
    const start = 50 * rank * rank;
    const next = 50 * Math.min(10, rank + 1) ** 2;
    const xp = rank >= 10 ? start : start + Math.round(hash(seed + i * 7) * (next - start - 1));
    return { key, value: a[key], aptitude, rank, xp, xpRankStart: start, xpNextRank: next, xpToNext: rank >= 10 ? 0 : next - xp };
  });
  const adult = age >= 16;
  return { skills, focus: adult ? keys[seed % keys.length] : null, focusActive: adult ? true : null, focusReadyDay: null };
}

function attrs(seed: number, bias: Partial<Attributes> = {}): Attributes {
  const keys: AttributeKey[] = ['martial', 'diplomacy', 'stewardship', 'intrigue', 'learning', 'prowess'];
  const out = {} as Attributes;
  keys.forEach((k, i) => { out[k] = Math.max(0, Math.min(20, Math.round(4 + hash(seed + i * 13) * 10 + (bias[k] ?? 0)))); });
  return out;
}

// ------------------------------------------------------------------ houses

interface HouseDef { id: Id; name: string; control: House['control']; headId: Id; prestige: number; seed: number }
const HOUSE_DEFS: HouseDef[] = [
  { id: 'h1', name: 'Varn', control: 'player', headId: 'c1', prestige: 120, seed: 4711 },
  { id: 'h2', name: 'Kessel', control: 'ai', headId: 'c10', prestige: 340, seed: 88231 },
  { id: 'h3', name: 'Orlow', control: 'ai', headId: 'c11', prestige: 720, seed: 1203 },
  { id: 'h4', name: 'Brandt', control: 'ai', headId: 'c12', prestige: 1500, seed: 90210 },
  { id: 'h5', name: 'Ashby', control: 'ai', headId: 'c16', prestige: 610, seed: 33017 },
];

// ------------------------------------------------------------------ characters

interface CharDef {
  id: Id; first: string; sex: 'M' | 'F'; age: number; house: Id; traits: string[];
  father?: Id; mother?: Id; spouse?: Id; children?: Id[]; knightOf?: Id; loc?: Id; bias?: Partial<Attributes>;
}
const CHAR_DEFS: CharDef[] = [
  { id: 'c1', first: 'Aldric', sex: 'M', age: 35, house: 'h1', traits: ['brave', 'just', 'veteran'], spouse: 'c2', children: ['c3', 'c4', 'c5'], loc: 't9', bias: { martial: 4 } },
  { id: 'c2', first: 'Maren', sex: 'F', age: 35, house: 'h1', traits: ['pious', 'calm', 'edu_stewardship'], spouse: 'c1', children: ['c3', 'c4', 'c5'], loc: 't9', bias: { stewardship: 4 } },
  { id: 'c3', first: 'Edric', sex: 'M', age: 18, house: 'h1', traits: ['ambitious', 'edu_martial', 'strong'], father: 'c1', mother: 'c2', knightOf: 't4', loc: 't8' },
  { id: 'c4', first: 'Isolde', sex: 'F', age: 17, house: 'h1', traits: ['honest', 'edu_diplomacy', 'beautiful'], father: 'c1', mother: 'c2', loc: 't9' },
  { id: 'c5', first: 'Tomas', sex: 'M', age: 16, house: 'h1', traits: ['content', 'craven', 'edu_learning'], father: 'c1', mother: 'c2', loc: 't9' },
  { id: 'c6', first: 'Wenna', sex: 'F', age: 9, house: 'h1', traits: ['calm'], father: 'c1', mother: 'c2', loc: 't9' },
  { id: 'c10', first: 'Hugo', sex: 'M', age: 48, house: 'h2', traits: ['greedy', 'arbitrary', 'wrathful'], spouse: 'c18', children: ['c13', 'c19'], loc: 't8' },
  { id: 'c18', first: 'Berthe', sex: 'F', age: 44, house: 'h2', traits: ['generous', 'pious', 'calm'], spouse: 'c10', children: ['c13', 'c19'], loc: 't8' },
  { id: 'c13', first: 'Adela', sex: 'F', age: 19, house: 'h2', traits: ['honest', 'brave', 'beautiful'], father: 'c10', mother: 'c18', loc: 't8' },
  { id: 'c19', first: 'Rolf', sex: 'M', age: 15, house: 'h2', traits: ['squire', 'craven'], father: 'c10', mother: 'c18', loc: 't8' },
  { id: 'c11', first: 'Konrad', sex: 'M', age: 52, house: 'h3', traits: ['just', 'content', 'calm'], loc: 't11' },
  { id: 'c14', first: 'Wendel', sex: 'M', age: 20, house: 'h3', traits: ['ambitious', 'deceitful', 'edu_intrigue'], father: 'c11', loc: 't11' },
  { id: 'c12', first: 'Leopold', sex: 'M', age: 60, house: 'h4', traits: ['cynical', 'greedy', 'ill'], children: ['c20'], loc: 't13' },
  { id: 'c20', first: 'Gisela', sex: 'F', age: 18, house: 'h4', traits: ['ambitious', 'genius'], father: 'c12', loc: 't13' },
  { id: 'c16', first: 'Osric', sex: 'M', age: 44, house: 'h5', traits: ['brave', 'wrathful', 'scarred'], children: ['c15'], loc: 't14' },
  { id: 'c15', first: 'Lena', sex: 'F', age: 21, house: 'h5', traits: ['pious', 'honest'], father: 'c16', loc: 't14' },
];
const houseName = (id: Id | null) => HOUSE_DEFS.find((h) => h.id === id)?.name ?? null;

// ------------------------------------------------------------------ chivalry (dubbing, squires, guards)

/** Per-character chivalry state. Squire lists, guard lists, captaincies and court links are derived from it. */
const CHIVALRY: Partial<Record<Id, { dubbed?: boolean; tier?: KnightTier; capacity?: number; squireMasterId?: Id; guardOf?: Id; squireXp?: number }>> = {
  c3: { dubbed: true, tier: 'bachelor', capacity: 1 },
  c10: { dubbed: true, tier: 'banneret', capacity: 2 },
  c11: { dubbed: true, tier: 'champion', capacity: 2 },
  c19: { squireMasterId: 'c3', squireXp: 58 },
  c4: { guardOf: 't20', squireXp: 71 },
  c5: { squireXp: 12 },
};
const GUARD_LIMIT: Record<TitleRank, number> = { barony: 2, county: 3, duchy: 4, kingdom: 6, city: 0 };
const isDubbed = (id: Id): boolean => !!CHAR_DEFS.find((c) => c.id === id)?.knightOf || !!CHIVALRY[id]?.dubbed;
const squireIdsOf = (knightId: Id): Id[] => Object.entries(CHIVALRY).filter(([, v]) => v?.squireMasterId === knightId).map(([id]) => id);
const guardIdsOf = (titleId: Id): Id[] => Object.entries(CHIVALRY).filter(([, v]) => v?.guardOf === titleId).map(([id]) => id);
/** A guarded title's captain: its first active knight (the sim picks the best prowess). */
const captainOf = (titleId: Id): Id | null => {
  const rank = TITLE_DEFS.find((t) => t.id === titleId)?.rank;
  if (!rank || GUARD_LIMIT[rank] === 0) return null;
  return CHAR_DEFS.find((c) => c.knightOf === titleId)?.id ?? null;
};
const squireRankOf = (xp: number): SquireRank => (xp >= SQUIRE_XP_MAX ? 'ready' : xp >= SQUIRE_XP_SENIOR ? 'senior' : xp >= SQUIRE_XP_SEASONED ? 'seasoned' : 'novice');
/** Replace a squire's placement (or clear it with null) while keeping training xp. */
function setPlacement(squireId: Id, placement: { squireMasterId?: Id; guardOf?: Id }): void {
  const cur: NonNullable<(typeof CHIVALRY)[Id]> = { ...CHIVALRY[squireId] };
  delete cur.squireMasterId;
  delete cur.guardOf;
  cur.squireXp = cur.squireXp ?? 0;
  CHIVALRY[squireId] = { ...cur, ...placement };
}
/** Spelled-out titled name, matching sim queries.titledNameOf: "Count Hugo of County of Ravensmoor"; house form for the landless. */
function titledName(d: CharDef): string {
  const primary = TITLE_DEFS.filter((t) => t.holder === d.id).sort((a, b) => rankVal(b.rank) - rankVal(a.rank))[0];
  if (primary) return `${titleHolderWord(primary.rank, d.sex)} ${d.first} of ${primary.name}`;
  return `${d.first} of House ${houseName(d.house)}`;
}

// ------------------------------------------------------------------ titles

interface TitleDef {
  id: Id; name: string; rank: TitleRank; parent: Id | null; holder: Id | null; color: string;
  x?: number; y?: number; terrain?: Terrain; capital?: Id;
}
const TITLE_DEFS: TitleDef[] = [
  { id: 't1', name: 'Kingdom of Aldermark', rank: 'kingdom', parent: null, holder: 'c12', color: '#8a6d3b', capital: 't11' },
  { id: 't2', name: 'Duchy of Varnholt', rank: 'duchy', parent: 't1', holder: 'c11', color: '#4f7596', capital: 't11' },
  { id: 't3', name: 'Duchy of Ostmere', rank: 'duchy', parent: 't1', holder: 'c16', color: '#9a4b44', capital: 't14' },
  { id: 't4', name: 'County of Ravensmoor', rank: 'county', parent: 't2', holder: 'c10', color: '#6d8fb0', capital: 't8' },
  { id: 't5', name: 'County of Highfen', rank: 'county', parent: 't2', holder: 'c11', color: '#3f5f7c', capital: 't11' },
  { id: 't6', name: 'County of Karsgard', rank: 'county', parent: 't3', holder: 'c16', color: '#b4635a', capital: 't14' },
  { id: 't7', name: 'County of Wellmouth', rank: 'county', parent: 't3', holder: 'c16', color: '#82403a', capital: 't17' },
  { id: 't8', name: 'Ravensmoor Keep', rank: 'barony', parent: 't4', holder: 'c10', color: '#6d8fb0', x: 200, y: 180, terrain: 'hills' },
  { id: 't9', name: 'Dunmere', rank: 'barony', parent: 't20', holder: 'c1', color: '#6d8fb0', x: 280, y: 270, terrain: 'plains' },
  { id: 't10', name: 'Saltwick', rank: 'city', parent: 't4', holder: 'c10', color: '#6d8fb0', x: 140, y: 320, terrain: 'coast' },
  { id: 't11', name: 'Highfen Hall', rank: 'barony', parent: 't5', holder: 'c11', color: '#3f5f7c', x: 420, y: 140, terrain: 'marsh' },
  { id: 't12', name: 'Fenwatch', rank: 'barony', parent: 't5', holder: 'c11', color: '#3f5f7c', x: 490, y: 250, terrain: 'marsh' },
  { id: 't13', name: 'Greywater', rank: 'city', parent: 't5', holder: 'c12', color: '#3f5f7c', x: 370, y: 330, terrain: 'plains' },
  { id: 't14', name: 'Karsgard Castle', rank: 'barony', parent: 't6', holder: 'c16', color: '#b4635a', x: 660, y: 190, terrain: 'mountains' },
  { id: 't15', name: 'Ironholt', rank: 'barony', parent: 't6', holder: 'c16', color: '#b4635a', x: 770, y: 290, terrain: 'mountains' },
  { id: 't16', name: 'Pinewald', rank: 'barony', parent: 't6', holder: 'c16', color: '#b4635a', x: 610, y: 340, terrain: 'forest' },
  { id: 't17', name: 'Wellmouth', rank: 'city', parent: 't7', holder: 'c16', color: '#82403a', x: 720, y: 500, terrain: 'coast' },
  { id: 't18', name: 'Oakhurst', rank: 'barony', parent: 't7', holder: 'c16', color: '#82403a', x: 560, y: 540, terrain: 'farmland' },
  { id: 't19', name: 'Stonebridge', rank: 'barony', parent: 't7', holder: 'c16', color: '#82403a', x: 850, y: 410, terrain: 'hills' },
  { id: 't20', name: 'County of Dunmark', rank: 'county', parent: 't2', holder: 'c1', color: '#5d86a8', capital: 't9' },
  { id: 't21', name: 'Marlowe Tower', rank: 'barony', parent: 't20', holder: 'c14', color: '#5d86a8', x: 330, y: 410, terrain: 'forest' },
];
const ROADS: Road[] = [
  { a: 't8', b: 't9', days: 3 }, { a: 't8', b: 't10', days: 4 }, { a: 't9', b: 't10', days: 4 }, { a: 't9', b: 't13', days: 4 }, { a: 't9', b: 't21', days: 3 }, { a: 't21', b: 't13', days: 3 },
  { a: 't8', b: 't11', days: 6 }, { a: 't11', b: 't12', days: 3 }, { a: 't12', b: 't13', days: 4 }, { a: 't11', b: 't13', days: 5 },
  { a: 't12', b: 't16', days: 5 }, { a: 't16', b: 't14', days: 4 }, { a: 't14', b: 't15', days: 3 }, { a: 't16', b: 't15', days: 4 },
  { a: 't16', b: 't18', days: 5 }, { a: 't18', b: 't17', days: 5 }, { a: 't17', b: 't19', days: 4 }, { a: 't15', b: 't19', days: 5 },
];

const constructions: Record<Id, { building: string; level: number; doneDay: number; doneHour?: number }> = { t9: { building: 'barracks', level: 1, doneDay: NOW_DAY + 200, doneHour: absHourOf(NOW_DAY + 200, 14) } };
const builtLevels: Record<Id, Record<string, number>> = { t9: { farmland: 1, market: 1 } };
const titleState: Record<Id, { taxRate: number; levyRate: number; council: Partial<Record<CouncilSeat, Id | null>> }> = {};
for (const t of TITLE_DEFS) {
  const council: Partial<Record<CouncilSeat, Id | null>> = {};
  for (const s of COUNCIL_SEATS[t.rank]) council[s] = null;
  titleState[t.id] = { taxRate: 0.3, levyRate: 0.5, council };
}
titleState.t4.council.steward = 'c10';
titleState.t4.council.marshal = 'c3';
titleState.t20.council.steward = 'c2';

function charView(d: CharDef): CharacterView {
  const heldTitleIds = TITLE_DEFS.filter((t) => t.holder === d.id).map((t) => t.id);
  const primary = TITLE_DEFS.filter((t) => t.holder === d.id)
    .sort((a, b) => rankVal(b.rank) - rankVal(a.rank))[0] ?? null;
  const knightTitle = d.knightOf ? TITLE_DEFS.find((t) => t.id === d.knightOf) : null;
  const seed = Number(d.id.slice(1));
  const council = TITLE_DEFS.flatMap((t) =>
    (Object.entries(titleState[t.id].council) as [CouncilSeat, Id | null][])
      .filter(([, c]) => c === d.id).map(([seat]) => ({ titleId: t.id, seat })));
  const army = armies.find((a) => a.commanderId === d.id);
  const chiv = CHIVALRY[d.id] ?? {};
  const dubbed = isDubbed(d.id);
  const prestige = 20 + Math.round(hash(seed + 5) * 200);
  const isHead = HOUSE_DEFS.some((h) => h.headId === d.id);
  const masterCourt = chiv.squireMasterId ? CHAR_DEFS.find((c) => c.id === chiv.squireMasterId)?.knightOf ?? null : null;
  const guardCaptainOf = TITLE_DEFS.find((t) => captainOf(t.id) === d.id)?.id ?? null;
  return {
    id: d.id, name: `${d.first} of House ${houseName(d.house)}`, titledName: titledName(d), firstName: d.first, sex: d.sex, age: d.age, alive: true,
    houseId: d.house, houseName: houseName(d.house),
    primaryTitle: primary ? `${titleHolderWord(primary.rank, d.sex)} of ${primary.name.replace(/^(Kingdom|Duchy|County) of /, '')}` : knightTitle ? `Knight of ${knightTitle.name.replace(/^(Kingdom|Duchy|County) of /, '')}` : null,
    primaryTitleId: primary?.id ?? knightTitle?.id ?? null,
    faceGenes: faceOverride[d.id] ?? genes(seed), traits: d.traits, isAI: d.house !== PLAYER_HOUSE, spouseId: d.spouse ?? null,
    isHead: HOUSE_DEFS.some((h) => h.headId === d.id),
    birthDay: NOW_DAY - d.age * 360 - (seed % 300), deathDay: null, deathCause: null,
    fatherId: d.father ?? null, motherId: d.mother ?? null, childIds: d.children ?? [], betrothedId: null,
    attributes: attrs(seed, d.bias),
    ...mockSkills(attrs(seed, d.bias), d.house === PLAYER_HOUSE, d.age, seed),
    health: d.house === PLAYER_HOUSE ? 4 + Math.round(hash(seed + 99) * 5) : null, healthStatus: 'healthy', prestige,
    heldTitleIds, councilSeats: council, knightOf: d.knightOf ?? null,
    squireOf: chiv.squireMasterId ? masterCourt : chiv.guardOf ?? null, locationId: d.loc ?? 't9', prisonerOf: null,
    squireMasterId: chiv.squireMasterId ?? null, guardOf: chiv.guardOf ?? null, guardCaptainOf,
    squireXp: chiv.squireXp ?? null, squireRank: chiv.squireXp === undefined ? null : squireRankOf(chiv.squireXp),
    squireIds: squireIdsOf(d.id), squireCapacity: dubbed ? chiv.capacity ?? 0 : 0, dubbed, knightTier: dubbed ? chiv.tier ?? 'bachelor' : null,
    standing: Math.round(prestige) + (isHead ? 100 : 0),
    commandingArmyId: army?.id ?? null, pregnant: d.id === 'c2' ? true : d.house === PLAYER_HOUSE ? false : null,
    pregnancy: d.id === 'c2' ? { dueDay: NOW_DAY + 40, dueHour: absHourOf(NOW_DAY + 40, 8) } : null,
    education: d.age >= 6 && d.age < 16 ? { focus: 'learning', tutorId: 'c2' } : null,
    opinionOfLiege: d.house === PLAYER_HOUSE ? 35 : null, claimTitleIds: d.id === 'c3' ? ['t8'] : [],
    controllable: d.house === PLAYER_HOUSE,
  };
}
function rankVal(r: TitleRank) { return { kingdom: 5, duchy: 4, county: 3, city: 2, barony: 1 }[r]; }
function titleHolderWord(r: TitleRank, sex: 'M' | 'F') {
  return ({ kingdom: ['King', 'Queen'], duchy: ['Duke', 'Duchess'], county: ['Count', 'Countess'], barony: ['Baron', 'Baroness'], city: ['Lord Mayor', 'Lady Mayor'] } as const)[r][sex === 'M' ? 0 : 1];
}
function summary(v: CharacterView): CharacterSummary {
  const { id, name, titledName, firstName, sex, age, alive, houseId, houseName: hn, primaryTitle, primaryTitleId, faceGenes, traits, isAI, spouseId, isHead } = v;
  return { id, name, titledName, firstName, sex, age, alive, houseId, houseName: hn, primaryTitle, primaryTitleId, faceGenes, traits, isAI, spouseId, isHead };
}

const troops = (t: Partial<Troops>): Troops => ({ levy: 0, spearmen: 0, archers: 0, lightCavalry: 0, heavyCavalry: 0, ...t });
const armySize = (t: Troops) => t.levy + t.spearmen + t.archers + t.lightCavalry + t.heavyCavalry;
const armies: ArmyView[] = [];
function addArmy(a: Omit<ArmyView, 'size' | 'ownerName' | 'commanderName'>) {
  const owner = CHAR_DEFS.find((c) => c.id === a.ownerId)!;
  const cmd = a.commanderId ? CHAR_DEFS.find((c) => c.id === a.commanderId) : null;
  armies.push({ ...a, ownerName: `${owner.first} of House ${houseName(owner.house)}`, commanderName: cmd ? `${cmd.first} of House ${houseName(cmd.house)}` : null, sergeant: !cmd, size: armySize(a.troops) });
}
addArmy({ id: 'a1', houseId: 'h1', ownerId: 'c1', commanderId: 'c3', troops: troops({ levy: 180, spearmen: 60, archers: 40 }), morale: 0.85, locationId: 't9', path: ['t13'], nextArrivalDay: NOW_DAY + 2, nextArrivalHour: absHourOf(NOW_DAY + 2, 9), formation: 'shield_wall', posture: 'defend_region', siege: null });
addArmy({ id: 'a2', houseId: 'h2', ownerId: 'c10', commanderId: null, troops: troops({ levy: 400, spearmen: 150, heavyCavalry: 40 }), morale: 0.9, locationId: 't8', path: [], nextArrivalDay: null, formation: 'line', posture: 'hold', siege: null });
addArmy({ id: 'a3', houseId: 'h5', ownerId: 'c16', commanderId: 'c16', troops: troops({ levy: 600, archers: 120, lightCavalry: 80 }), morale: 0.7, locationId: 't12', path: [], nextArrivalDay: null, formation: 'wedge', posture: 'aggressive', siege: { holdingId: 't12', progress: 0.35, startHour: absHourOf(NOW_DAY - 6, 12), doneHour: absHourOf(NOW_DAY + 3, 12) } });

const wars: WarView[] = [
  { id: 'w1', name: 'War of the Fenwatch Toll', attackerId: 'c16', defenderId: 'c11', attackerAllyIds: [], defenderAllyIds: [], goal: { type: 'tribute', titleId: null }, startDay: NOW_DAY - 400, score: -35, endedDay: null, outcome: null, justified: false, attackerName: 'Osric of House Ashby', defenderName: 'Konrad of House Orlow' },
  { id: 'w2', name: 'Varn Claim on Pinewald', attackerId: 'c1', defenderId: 'c16', attackerAllyIds: ['c10'], defenderAllyIds: [], goal: { type: 'seize_title', titleId: 't16' }, startDay: NOW_DAY - 120, score: 22, endedDay: null, outcome: null, justified: true, attackerName: 'Aldric of House Varn', defenderName: 'Osric of House Ashby' },
  { id: 'w3', name: 'Tollgate Quarrel', attackerId: 'c1', defenderId: 'c10', attackerAllyIds: [], defenderAllyIds: ['c12'], goal: { type: 'humiliate', titleId: null }, startDay: NOW_DAY - 2000, score: 41, endedDay: NOW_DAY - 1700, outcome: 'attacker_won', justified: true, attackerName: 'Aldric of House Varn', defenderName: 'Hugo of House Kessel' },
];

function titleSummary(d: TitleDef): TitleSummary {
  const h = d.holder ? CHAR_DEFS.find((c) => c.id === d.holder)! : null;
  const isHolding = d.x !== undefined;
  const built: Record<string, number> = builtLevels[d.id] ?? {};
  return {
    id: d.id, name: d.name, rank: d.rank, holderId: d.holder, holderName: h ? titledName(h) : null,
    holderHouseId: h?.house ?? null, liegeTitleId: d.parent, deJureParentId: d.parent, capitalId: d.capital ?? null, color: d.color,
    holding: isHolding ? {
      x: d.x!, y: d.y!, terrain: d.terrain!, development: 2 + Math.round(hash(Number(d.id.slice(1))) * 5), buildings: built,
      construction: constructions[d.id] ?? null,
      garrison: 40 + Math.round(hash(Number(d.id.slice(1)) + 3) * 80), levyPool: 60 + Math.round(hash(Number(d.id.slice(1)) + 9) * 200), occupiedBy: d.id === 't12' ? 'a3' : null,
    } : null,
  };
}

function houseSummary(d: HouseDef): HouseSummary {
  const head = d.headId;
  const held = TITLE_DEFS.filter((t) => t.holder === head).sort((a, b) => rankVal(b.rank) - rankVal(a.rank));
  return {
    id: d.id, name: d.name, control: d.control, isAI: d.control !== 'player', wasPlayer: d.control === 'player', headId: head, extinct: false,
    prestige: d.prestige, memberCount: CHAR_DEFS.filter((c) => c.house === d.id).length, heraldrySeed: d.seed, highestRank: held[0]?.rank ?? 'landless',
  };
}

let nextDayAtMs = Date.now() + 95_000;
function world(): WorldView {
  while (nextDayAtMs < Date.now()) nextDayAtMs += MS_PER_GAME_DAY;
  const day = NOW_DAY + Math.floor((Date.now() - mockStart) / MS_PER_GAME_DAY);
  return {
    day, date: dayToDate(day), nextDayAtMs, msPerDay: MS_PER_GAME_DAY, msPerHour: MS_PER_GAME_HOUR, kingdomId: 't1', kingdomIds: ['t1'],
    titles: TITLE_DEFS.map(titleSummary), roads: ROADS, houses: HOUSE_DEFS.filter((h) => h.id !== PLAYER_HOUSE || founded).map(houseSummary),
    armies, wars,
  };
}
const mockStart = Date.now();

// ------------------------------------------------------------------ house view / letters / chronicle

const proposals: { in: Proposal[]; out: Proposal[] } = {
  in: [
    { id: 'p1', kind: 'marriage', fromId: 'c10', toId: 'c1', subjectAId: 'c13', subjectBId: 'c3', matrilineal: false, dowry: 60, warId: null, titleId: null, sentDay: NOW_DAY - 12, arriveDay: NOW_DAY - 8, expiresDay: NOW_DAY + 70, status: 'pending' },
    { id: 'p2', kind: 'alliance', fromId: 'c11', toId: 'c1', subjectAId: null, subjectBId: null, matrilineal: false, dowry: 0, warId: null, titleId: null, sentDay: NOW_DAY - 4, arriveDay: NOW_DAY - 1, expiresDay: NOW_DAY + 85, status: 'pending' },
    { id: 'p4', kind: 'peace', fromId: 'c16', toId: 'c1', subjectAId: null, subjectBId: null, matrilineal: false, dowry: 0, warId: 'w2', titleId: null, terms: 'white_peace', sentDay: NOW_DAY - 1, arriveDay: NOW_DAY + 3, expiresDay: NOW_DAY + 90, status: 'pending' },
  ],
  out: [
    { id: 'p5', kind: 'marriage', fromId: 'c1', toId: 'c11', subjectAId: 'c4', subjectBId: 'c14', matrilineal: true, dowry: 40, warId: null, titleId: null, sentDay: NOW_DAY - 60, arriveDay: NOW_DAY - 55, expiresDay: NOW_DAY - 5, status: 'declined' },
    { id: 'p3', kind: 'service', fromId: 'c1', toId: 'c10', subjectAId: 'c3', subjectBId: null, matrilineal: false, dowry: 0, warId: null, titleId: 't4', sentDay: NOW_DAY - 20, arriveDay: NOW_DAY - 17, expiresDay: NOW_DAY + 60, status: 'pending' },
  ],
};
const letters: Letter[] = [
  { id: 'l1', fromId: 'c10', toId: 'c1', text: 'Aldric, the roads near Fenwatch are no longer safe. I expect your levies at Ravensmoor before the first frost. -- Hugo', sentDay: NOW_DAY - 6, arriveDay: NOW_DAY - 3, read: false },
  { id: 'l2', fromId: 'c1', toId: 'c11', text: 'My Lord Duke, I write to ask for the hand of your son Wendel for my daughter Isolde.', sentDay: NOW_DAY - 30, arriveDay: NOW_DAY - 26, read: true },
  { id: 'l3', fromId: 'c1', toId: 'c10', text: 'The harvest at Dunmere will be thin this year. I ask your leave to delay the tithe.', sentDay: NOW_DAY - 2, arriveDay: NOW_DAY + 1, read: false },
];
const KINDS: ChronicleKind[] = ['birth', 'death', 'marriage', 'coming_of_age', 'war_declared', 'battle', 'siege', 'construction', 'council', 'event', 'title_granted', 'knighted'];
const chronicleAll: ChronicleEntry[] = Array.from({ length: 140 }, (_, i) => {
  const kind = KINDS[i % KINDS.length];
  const mine = i % 3 !== 1;
  return {
    id: 1000 - i, day: NOW_DAY - i * 9, hour: (i * 5) % 24, kind,
    text: ({
      birth: 'A child is born to Aldric and Maren of House Varn.', death: 'Old Berthold of House Kessel died in his sleep.', marriage: 'Wendel Orlow was wed to a daughter of House Ashby.',
      coming_of_age: 'Tomas of House Varn came of age.', war_declared: 'Osric Ashby declared war upon Konrad Orlow.', battle: 'A skirmish at the Fenwatch ford left forty dead.',
      siege: 'Fenwatch is besieged by the army of Osric Ashby.', construction: 'A barracks was completed at Dunmere.', council: 'Hugo Kessel appointed Edric Varn as his marshal.',
      event: 'A hard winter settles on the marches. Grain is dear.', title_granted: 'The barony of Dunmere was granted to Aldric Varn.', knighted: 'Edric Varn was knighted in the service of Count Hugo.',
    } as Record<string, string>)[kind] ?? 'Something happened.',
    houseIds: mine ? [PLAYER_HOUSE] : ['h3'], characterIds: [], important: i % 4 === 0,
  };
});

const standing = { ...DEFAULT_STANDING_ORDERS };
let playerLetters = letters;

function houseView(): HouseView {
  const def = HOUSE_DEFS[0];
  const members = CHAR_DEFS.filter((c) => c.house === PLAYER_HOUSE).map(charView);
  const house: House = {
    id: def.id, name: def.name, ownerAccountId: MY_ACCOUNT, control: 'player', wasPlayer: true, foundedDay: NOW_DAY - 10, headId: def.headId, extinct: false, extinctDay: null,
    resources: { ...START_STATE.resources, gold: 212, food: 86, timber: 34, stone: 4, iron: 9, horses: 3 }, prestige: def.prestige, standingOrders: standing,
    lastActiveDay: NOW_DAY, heraldrySeed: def.seed,
  };
  return {
    house, members, relatives: [summary(charView(CHAR_DEFS.find((c) => c.id === 'c13')!))], heldTitleIds: TITLE_DEFS.filter((t) => t.holder === 'c1').map((t) => t.id),
    armies: armies.filter((a) => a.houseId === PLAYER_HOUSE), wars: wars.filter((w) => w.attackerId === 'c1' || w.defenderId === 'c1'),
    schemes: [], plotsAgainstUs: [],
    incomingProposals: proposals.in, outgoingProposals: proposals.out, letters: playerLetters, chronicle: chronicleAll.filter((e) => e.houseIds.includes(PLAYER_HOUSE)).slice(0, 12),
    monthlyBalance: { gold: 8.5, food: -4, timber: 2, stone: 0, iron: 0.5, horses: 0.2, wool: 1, manpower: 3 }, standingOrders: standing,
    inactivity: { stage: mode === 'ended' ? 'caretaker' : 'active', realDaysInactive: 0 }, allyHouseIds: ['h2'], napHouseIds: ['h3'],
    factions: [],
    hooks: [],
  };
}

const kingdomLaws: RealmLaws = { succession: 'elective', gender: 'male_preference', crownAuthority: 1, taxCap: 0.4, internalWarsBanned: false };

function titleView(id: Id): TitleView {
  const d = TITLE_DEFS.find((t) => t.id === id);
  if (!d) throw new MockError(404, 'No such title');
  const s = titleSummary(d);
  const st = titleState[id];
  const kingdom = d.rank === 'kingdom';
  const charName = (cid: Id | null | undefined) => {
    const c = CHAR_DEFS.find((x) => x.id === cid);
    return c ? titledName(c) : null;
  };
  return {
    ...s,
    laws: kingdom ? kingdomLaws : null,
    council: COUNCIL_SEATS[d.rank].map((seat) => ({ seat, charId: st.council[seat] ?? null, charName: charName(st.council[seat]) })),
    councilCandidateIds: d.holder
      ? CHAR_DEFS.filter((c) => c.age >= 16 && c.id !== d.holder && (c.house === CHAR_DEFS.find((h) => h.id === d.holder)?.house || c.knightOf === id)
        && !Object.values(titleState).some((ts) => Object.values(ts.council).includes(c.id))).map((c) => c.id)
      : [],
    vassalTaxRate: st.taxRate, vassalLevyRate: st.levyRate,
    vassalTitleIds: TITLE_DEFS.filter((t) => t.parent === id).map((t) => t.id),
    knightIds: CHAR_DEFS.filter((c) => c.knightOf === id).map((c) => c.id),
    guardIds: guardIdsOf(id), guardLimit: GUARD_LIMIT[d.rank], guardCaptainId: captainOf(id), knightLimit: MOCK_KNIGHT_LIMIT[d.rank],
    claimantIds: id === 't8' ? ['c3'] : [], heirId: d.holder ? (CHAR_DEFS.find((c) => c.id === d.holder)?.children?.[0] ?? null) : null,
    elective: kingdom ? { electorIds: ['c10', 'c11', 'c16', 'c3'], candidateIds: ['c12', 'c11', 'c16'], votes: { c10: 'c11', c16: 'c11' } } : null,
    monthlyIncome: 6 + Math.round(hash(Number(id.slice(1))) * 14),
    election: d.rank === 'city' ? cityElectionView(id) : undefined,
  };
}

// ------------------------------------------------------------------ history views

const TENURE_START = NOW_DAY - 360 * 40;

function historyNode(d: CharDef): HistoryCharacter {
  return {
    id: d.id, name: `${d.first} of House ${houseName(d.house)}`, titledName: titledName(d), gender: d.sex,
    birthDay: NOW_DAY - d.age * 360, deathDay: null, houseId: d.house, houseName: houseName(d.house),
    fatherId: d.father ?? null, motherId: d.mother ?? null, spouseId: d.spouse ?? null,
    childIds: d.children ? [...d.children] : [],
    isHead: HOUSE_DEFS.some((h) => h.headId === d.id),
  };
}

function titleTenure(d: TitleDef): TitleTenureView {
  const h = d.holder ? CHAR_DEFS.find((c) => c.id === d.holder) : null;
  return {
    holderId: d.holder!, holderName: h ? titledName(h) : null,
    houseId: h?.house ?? null, houseName: houseName(h?.house ?? null),
    startDay: TENURE_START, endDay: null, reason: 'creation',
  };
}

function characterHistory(d: CharDef): CharacterHistory {
  return {
    character: historyNode(d),
    titles: TITLE_DEFS.filter((t) => t.holder === d.id).map(titleTenure),
    chronicle: chronicleAll.filter((e) => e.houseIds.includes(d.house)).slice(0, 100),
  };
}

function searchCharacters(params: URLSearchParams): HistoryCharacter[] {
  const q = (params.get('q') ?? '').toLowerCase();
  if (!q) throw new MockError(400, 'q: must be at least 1 character');
  const alive = params.get('alive');
  const houseId = params.get('houseId');
  const rank = params.get('rank');
  const limit = Math.min(Number(params.get('limit') ?? 25) || 25, 100);
  const fullName = (d: CharDef) => `${d.first} of House ${houseName(d.house)}`;
  const heldRank = (d: CharDef): TitleRank | null => {
    const held = TITLE_DEFS.filter((t) => t.holder === d.id);
    return held.length ? held.reduce((a, t) => (rankVal(t.rank) > rankVal(a) ? t.rank : a), held[0]!.rank) : null;
  };
  const hits = CHAR_DEFS
    .filter((d) => fullName(d).toLowerCase().includes(q))
    .filter((d) => alive === null || alive === '1' || alive === 'true')
    .filter((d) => !houseId || d.house === houseId)
    .filter((d) => !rank || TITLE_DEFS.some((t) => t.holder === d.id && t.rank === rank))
    .sort((a, b) => {
      const ra = heldRank(a); const rb = heldRank(b);
      if ((ra !== null) !== (rb !== null)) return ra !== null ? -1 : 1;
      if (ra && rb && rankVal(ra) !== rankVal(rb)) return rankVal(rb) - rankVal(ra);
      return fullName(a).localeCompare(fullName(b));
    });
  return hits.slice(0, limit).map(historyNode);
}

// ------------------------------------------------------------------ commands

let cmdSeq = 0;
/** Knights who already petitioned in the mock (one petition per year). */
const petitioned = new Set<string>();
const commandsById = new Map<string, QueuedCommand>();

// ------------------------------------------------------------------ merchant state (markets, guilds, companies, loans)

/** The realm's three cities: a city's id IS its city-rank title id. */
const CITY_IDS: Id[] = ['t10', 't13', 't17'];
const cityTariffs: Record<Id, number> = { t10: 0.05, t13: 0, t17: 0.02 };
const makerGold: Record<Id, number> = { t10: 240, t13: 160, t17: 120 };
const cityLastPrices: Record<Id, Partial<Record<TradableResource, number>>> = { t10: { food: 2, timber: 3 }, t13: { stone: 4 }, t17: {} };
/** Guild xp per house per city. Player house (h1) tops Saltwick's ladder. */
const guildXp: Record<Id, { houseId: Id; xp: number }[]> = {
  t10: [{ houseId: 'h1', xp: 500 }, { houseId: 'h2', xp: 340 }, { houseId: 'h3', xp: 90 }],
  t13: [{ houseId: 'h4', xp: 300 }, { houseId: 'h1', xp: 45 }, { houseId: 'h3', xp: 40 }],
  t17: [{ houseId: 'h5', xp: 70 }],
};
function guildRankOf(xp: number): number {
  return xp >= GUILD_XP_GUILDMASTER ? 3 : xp >= GUILD_XP_MASTER ? 2 : xp >= GUILD_XP_JOURNEYMAN ? 1 : 0;
}
let orderSeq = 0;
const marketOrders: Record<Id, MyMarketOrderView[]> = {
  // Player house has an open buy order with gold in escrow; a Kessel sell order sits in the public book.
  t10: [
    { id: 'o1', houseId: 'h1', houseName: 'Varn', side: 'buy', resource: 'food', remaining: 20, unitPrice: 2, placedDay: NOW_DAY - 4, expiresDay: NOW_DAY + MARKET_ORDER_EXPIRY_DAYS - 4, escrowGold: 40 },
    { id: 'o2', houseId: 'h2', houseName: 'Kessel', side: 'sell', resource: 'timber', remaining: 30, unitPrice: 4, placedDay: NOW_DAY - 2, expiresDay: NOW_DAY + MARKET_ORDER_EXPIRY_DAYS - 2, escrowUnits: 30 },
  ],
  t13: [],
  t17: [],
};
/** Votes cast in the city election (elector character id -> candidate character id). */
const electionVotes: Record<Id, Record<Id, Id>> = { t10: { c10: 'c10', c16: 'c1' }, t13: {}, t17: {} };
/** The player house's own vote, when cast. */
let myVote: Id | null = null;
const companiesState: CompanyView[] = [
  { id: 'co1', name: 'Woven Words', cityId: 't10', cityName: 'Saltwick', ownerId: 'c2', ownerName: 'Maren of House Varn', chain: 'weaver', licenceLevel: 2, managerId: 'c5', managerName: 'Tomas of House Varn', wages: COMPANY_WAGES[2], foundedDay: NOW_DAY - 800 },
  { id: 'co2', name: 'Ironworks of Greywater', cityId: 't13', cityName: 'Greywater', ownerId: 'c12', ownerName: 'Leopold of House Brandt', chain: 'smelter', licenceLevel: 1, managerId: null, managerName: null, wages: COMPANY_WAGES[1], foundedDay: NOW_DAY - 300 },
];
interface MockLoan {
  id: Id; lenderHouseId: Id; lenderName: string; borrowerHouseId: Id; borrowerName: string;
  principal: number; outstanding: number; annualRate: number; issuedDay: number; missed: number; defaulted: boolean;
}
const loansState: MockLoan[] = [
  { id: 'ln1', lenderHouseId: 'h1', lenderName: 'House Varn', borrowerHouseId: 'h2', borrowerName: 'House Kessel', principal: 100, outstanding: 110, annualRate: 0.1, issuedDay: NOW_DAY - 90, missed: 0, defaulted: false },
  { id: 'ln2', lenderHouseId: 'h3', lenderName: 'House Orlow', borrowerHouseId: 'h1', borrowerName: 'House Varn', principal: 50, outstanding: 30, annualRate: 0.2, issuedDay: NOW_DAY - 60, missed: 1, defaulted: false },
];

function cityDef(id: Id): TitleDef | undefined {
  return TITLE_DEFS.find((t) => t.id === id && t.rank === 'city');
}

/** Road-days from the player house's seat ('t9') to a city, via the mock ROADS graph; null when unreachable. */
function roadDaysTo(id: Id): number | null {
  const dist = new Map<Id, number>([['t9', 0]]);
  for (let i = 0; i < ROADS.length; i++) {
    for (const r of ROADS) {
      for (const [x, y] of [[r.a, r.b], [r.b, r.a]]) {
        const dx = dist.get(x);
        if (dx !== undefined && dx + r.days < (dist.get(y) ?? Infinity)) dist.set(y, dx + r.days);
      }
    }
  }
  return dist.get(id) ?? null;
}

function deliveriesView(): DeliveriesView {
  const rows: DeliveryRow[] = [
    { id: 'dv1', resource: 'wool', quantity: 12, fromCityId: 't10', fromCityName: 'Saltwick', arriveDay: NOW_DAY + (roadDaysTo('t10') ?? CARAVAN_UNREACHABLE_DAYS) },
    { id: 'dv2', resource: 'stone', quantity: 8, fromCityId: 't13', fromCityName: 'Greywater', arriveDay: NOW_DAY + (roadDaysTo('t13') ?? CARAVAN_UNREACHABLE_DAYS) + 1 },
  ];
  return { houseId: PLAYER_HOUSE, deliveries: rows };
}

function citiesView(): CitiesView {
  return {
    cities: CITY_IDS.map((id) => {
      const d = cityDef(id)!;
      const holder = d.holder ? CHAR_DEFS.find((c) => c.id === d.holder) : undefined;
      const myXp = guildXp[id]?.find((r) => r.houseId === PLAYER_HOUSE)?.xp ?? 0;
      return {
        myGuildXp: myXp, myGuildRank: guildRankOf(myXp), nextElectionDay: NOW_DAY + 200 + Number(id.slice(1)) * 30,
        cityId: id, name: d.name, holderHouseName: houseName(holder?.house ?? null),
        development: 5 + Math.round(hash(Number(id.slice(1))) * 10),
        tariffRate: cityTariffs[id], orderCount: marketOrders[id].length, roadDays: roadDaysTo(id),
      };
    }),
  };
}

function marketView(id: Id): MarketView {
  const d = cityDef(id);
  if (!d) throw new MockError(404, 'No such city');
  const holder = d.holder ? CHAR_DEFS.find((c) => c.id === d.holder) : undefined;
  const dev = 5 + Math.round(hash(Number(id.slice(1))) * 10);
  const quotes = {} as Record<TradableResource, MarketQuote>;
  const stock: Partial<Record<TradableResource, number>> = {};
  for (const r of TRADABLE_RESOURCES) {
    quotes[r] = { base: BASE_PRICES[r], bid: Math.max(1, BASE_PRICES[r] - MAKER_TICK), ask: BASE_PRICES[r] + MAKER_TICK };
    stock[r] = MAKER_STOCK_BASE + dev;
  }
  const orders = marketOrders[id].map(({ houseId, escrowGold, escrowUnits, ...pub }) => pub);
  const myOrders = marketOrders[id].filter((o) => o.houseId === PLAYER_HOUSE).map((o) => ({ ...o }));
  const rd = roadDaysTo(id);
  return {
    cityId: id, cityName: d.name, holderHouseId: holder?.house ?? null, holderHouseName: houseName(holder?.house ?? null),
    development: dev, tariffRate: cityTariffs[id], quotes, lastPrices: cityLastPrices[id], orders, myOrders,
    makerGold: makerGold[id], makerStock: stock, roadDays: rd, deliveryDays: rd == null ? null : Math.max(1, rd * CARAVAN_DAYS_PER_ROAD_DAY),
  };
}

function guildView(id: Id): GuildView {
  const d = cityDef(id);
  if (!d) throw new MockError(404, 'No such city');
  const ladder = (guildXp[id] ?? [])
    .map((r) => ({ houseId: r.houseId, houseName: houseName(r.houseId) ?? '', xp: r.xp, rank: guildRankOf(r.xp) }))
    .sort((a, b) => b.xp - a.xp);
  const mine = ladder.find((r) => r.houseId === PLAYER_HOUSE);
  return {
    cityId: id, cityName: d.name, nextElectionDay: NOW_DAY + 200 + Number(id.slice(1)) * 30, myXp: mine?.xp, myRank: mine?.rank, ladder,
    thresholds: { journeyman: GUILD_XP_JOURNEYMAN, master: GUILD_XP_MASTER, guildmaster: GUILD_XP_GUILDMASTER },
  };
}

function cityElectionView(id: Id): CityElectionView {
  const candidates = (guildXp[id] ?? []).map((r) => {
    const h = HOUSE_DEFS.find((x) => x.id === r.houseId);
    const c = CHAR_DEFS.find((x) => x.id === h?.headId);
    return {
      characterId: c?.id ?? '', name: c ? titledName(c) : '',
      votes: Object.values(electionVotes[id]).filter((v) => v === c?.id).length,
      houseId: r.houseId, houseName: houseName(r.houseId),
    };
  });
  const myRow = (guildXp[id] ?? []).find((r) => r.houseId === PLAYER_HOUSE);
  return {
    nextElectionDay: NOW_DAY + 200, candidates, myVote: myVote ?? undefined,
    myFranchise: (myRow ? guildRankOf(myRow.xp) : 0) >= 3, termDays: CITY_TERM_DAYS,
  };
}

function loansView(): LoansView {
  return {
    loans: loansState.map((l) => ({ ...l, role: l.lenderHouseId === PLAYER_HOUSE ? 'lender' as const : 'borrower' as const })),
  };
}

function applyMock(cmd: Command): CommandResult {
  switch (cmd.type) {
    case 'declare_war': {
      if (cmd.goal === 'seize_title' || cmd.goal === 'depose') return { ok: false, error: 'You lack a claim on that title and have no casus belli against this ruler.' };
      const target = CHAR_DEFS.find((c) => c.id === cmd.targetCharId);
      const me = CHAR_DEFS.find((c) => c.id === cmd.actorId);
      const war: WarView = {
        id: `w${10 + cmdSeq}`, name: `War of ${me?.first ?? 'Varn'} against ${target?.first ?? 'the realm'}`, attackerId: cmd.actorId, defenderId: cmd.targetCharId,
        attackerAllyIds: [], defenderAllyIds: [], goal: { type: cmd.goal, titleId: cmd.titleId }, startDay: NOW_DAY, score: 0, endedDay: null, outcome: null, justified: false,
        attackerName: `${me?.first} of House ${houseName(me?.house ?? null)}`, defenderName: `${target?.first} of House ${houseName(target?.house ?? null)}`,
      };
      wars.push(war);
      return { ok: true, createdId: war.id };
    }
    case 'answer_proposal': {
      proposals.in = proposals.in.filter((p) => p.id !== cmd.proposalId);
      return { ok: true };
    }
    case 'send_letter': {
      const l: Letter = { id: `l${100 + cmdSeq}`, fromId: cmd.actorId, toId: cmd.toId, text: cmd.text, sentDay: NOW_DAY, arriveDay: NOW_DAY + 3, read: false };
      playerLetters = [l, ...playerLetters];
      return { ok: true, createdId: l.id };
    }
    case 'set_standing_orders': Object.assign(standing, cmd.orders); return { ok: true };
    case 'offer_service': {
      const k = CHAR_DEFS.find((c) => c.id === cmd.knightId);
      if (!k || k.house !== PLAYER_HOUSE) return { ok: false, error: 'Choose a living member of your house.' };
      if (k.age < 16) return { ok: false, error: 'Only adults can enter service.' };
      if (!isDubbed(k.id)) return { ok: false, error: 'Only a dubbed knight can enter service — train them as a squire first.' };
      return { ok: true };
    }
    case 'send_squire': {
      const squire = CHAR_DEFS.find((c) => c.id === cmd.squireId);
      const knight = CHAR_DEFS.find((c) => c.id === cmd.knightId);
      if (!squire || !knight || !isDubbed(knight.id)) return { ok: false, error: 'That knight is not dubbed.' };
      if (squireIdsOf(knight.id).length >= (CHIVALRY[knight.id]?.capacity ?? 0)) return { ok: false, error: `${knight.first} keeps no more squires.` };
      setPlacement(squire.id, { squireMasterId: knight.id });
      return { ok: true };
    }
    case 'dismiss_squire': {
      const chiv = CHIVALRY[cmd.squireId];
      if (!chiv?.squireMasterId && !chiv?.guardOf) return { ok: false, error: 'That character is not serving as a squire.' };
      const master = chiv.squireMasterId ? CHAR_DEFS.find((c) => c.id === chiv.squireMasterId) : undefined;
      const holder = chiv.guardOf ? TITLE_DEFS.find((t) => t.id === chiv.guardOf)?.holder : null;
      const holderHouse = holder ? CHAR_DEFS.find((c) => c.id === holder)?.house : null;
      const mine = master ? master.house === PLAYER_HOUSE : holderHouse === PLAYER_HOUSE;
      if (!mine) return { ok: false, error: 'Only the squire\'s master or his house can release him.' };
      setPlacement(cmd.squireId, {});
      return { ok: true };
    }
    case 'join_guard': {
      const squire = CHAR_DEFS.find((c) => c.id === cmd.squireId);
      const title = TITLE_DEFS.find((t) => t.id === cmd.titleId);
      if (!squire || squire.house !== PLAYER_HOUSE || squire.age < 12 || (squire.age > 21 && CHIVALRY[squire.id]?.squireXp === undefined) || isDubbed(squire.id) || TITLE_DEFS.some((t) => t.holder === squire.id)) {
        return { ok: false, error: 'Choose a living member of your house aged 12 to 21 (or already in training) who holds no land and is not yet a knight.' };
      }
      if (!title) return { ok: false, error: 'Unknown title.' };
      if (GUARD_LIMIT[title.rank] === 0) return { ok: false, error: 'That title keeps no Guard.' };
      // Our own titles plus their liege chain (the sim's rule for whose Guard a house may join).
      const chain = new Set<Id>();
      for (const o of TITLE_DEFS.filter((t) => t.holder && CHAR_DEFS.find((c) => c.id === t.holder)?.house === PLAYER_HOUSE)) {
        let cur: Id | null = o.id;
        while (cur && !chain.has(cur)) { chain.add(cur); cur = TITLE_DEFS.find((t) => t.id === cur)?.parent ?? null; }
      }
      if (!chain.has(title.id)) return { ok: false, error: 'You can only join the Guard of a title your house or your liege holds.' };
      if (guardIdsOf(title.id).length >= GUARD_LIMIT[title.rank]) return { ok: false, error: 'That Guard is already full.' };
      setPlacement(squire.id, { guardOf: title.id });
      return { ok: true };
    }
    case 'set_vassal_rates': titleState[cmd.titleId].taxRate = cmd.taxRate; titleState[cmd.titleId].levyRate = cmd.levyRate; return { ok: true };
    case 'appoint_council': titleState[cmd.titleId].council[cmd.seat] = cmd.charId; return { ok: true };
    case 'set_law':
      if (cmd.law === 'internalWarsBanned' && cmd.value === true && kingdomLaws.crownAuthority < 2) return { ok: false, error: 'Banning internal wars needs crown authority of at least 2.' };
      (kingdomLaws as unknown as Record<string, unknown>)[cmd.law] = cmd.value;
      return { ok: true };
    case 'build': {
      if (constructions[cmd.holdingId]) return { ok: false, error: 'Another building project is already under way here.' };
      const lvl = (builtLevels[cmd.holdingId]?.[cmd.building] ?? 0) + 1;
      constructions[cmd.holdingId] = { building: cmd.building, level: lvl, doneDay: NOW_DAY + 360 * lvl };
      return { ok: true };
    }
    case 'grant_title': {
      const d = TITLE_DEFS.find((x) => x.id === cmd.titleId);
      if (d) d.holder = cmd.toCharId;
      return { ok: true };
    }
    case 'revoke_title': {
      const d = TITLE_DEFS.find((x) => x.id === cmd.titleId);
      if (d) d.holder = null;
      return { ok: true };
    }
    case 'raise_army': {
      if (armies.some((a) => a.houseId === PLAYER_HOUSE && a.id.startsWith('a9'))) return { ok: false, error: 'Your levies are already raised.' };
      addArmy({ id: `a9${cmdSeq}`, houseId: PLAYER_HOUSE, ownerId: cmd.actorId, commanderId: cmd.commanderId, troops: troops({ levy: 240 }), morale: 1, locationId: 't9', path: [], nextArrivalDay: null, formation: 'line', posture: 'hold', siege: null });
      return { ok: true };
    }
    case 'recruit': {
      const a = armies.find((x) => x.id === cmd.armyId);
      if (!a) return { ok: false, error: 'No such army.' };
      for (const [k, n] of Object.entries(cmd.troops)) a.troops[k as keyof Troops] += n ?? 0;
      a.size = armySize(a.troops);
      return { ok: true };
    }
    case 'offer_peace': {
      const w = wars.find((x) => x.id === cmd.warId);
      if (!w) return { ok: false, error: 'No such war.' };
      w.endedDay = NOW_DAY; w.outcome = cmd.terms === 'surrender' ? 'defender_won' : 'white_peace';
      return { ok: true };
    }
    case 'configure_army': {
      const a = armies.find((x) => x.id === cmd.armyId);
      if (a) {
        if (cmd.formation) a.formation = cmd.formation;
        if (cmd.posture) a.posture = cmd.posture;
        if (cmd.commanderId !== undefined) { a.commanderId = cmd.commanderId; const c = CHAR_DEFS.find((x) => x.id === cmd.commanderId); a.commanderName = c ? `${c.first} of House ${houseName(c.house)}` : null; a.sergeant = !c; }
      }
      return { ok: true };
    }
    case 'move_army': {
      const a = armies.find((x) => x.id === cmd.armyId);
      if (a) { a.path = [cmd.targetId]; a.nextArrivalDay = NOW_DAY + 4; a.nextArrivalHour = absHourOf(NOW_DAY + 4, 9); }
      return { ok: true };
    }
    case 'disband_army': {
      const i = armies.findIndex((x) => x.id === cmd.armyId);
      if (i >= 0) armies.splice(i, 1);
      return { ok: true };
    }
    case 'petition_liege': {
      if (petitioned.has(cmd.actorId)) return { ok: false, error: 'You have already petitioned this year. Wait before asking again.' };
      petitioned.add(cmd.actorId);
      return { ok: true };
    }
    case 'set_head': {
      return { ok: false, error: 'Your head of house holds titles; he cannot be replaced.' };
    }
    case 'place_market_order': {
      if (!cityDef(cmd.cityId)) return { ok: false, error: 'No such city.' };
      if (cmd.quantity < 1 || cmd.quantity > MAX_MARKET_ORDER_QTY) return { ok: false, error: 'Quantity must be between 1 and 100.' };
      if (cmd.unitPrice < 1) return { ok: false, error: 'Unit price must be at least 1.' };
      const order: MyMarketOrderView = {
        id: `o${++orderSeq}`, houseId: PLAYER_HOUSE, houseName: 'Varn', side: cmd.side, resource: cmd.resource,
        remaining: cmd.quantity, unitPrice: cmd.unitPrice, placedDay: NOW_DAY, expiresDay: NOW_DAY + MARKET_ORDER_EXPIRY_DAYS,
        ...(cmd.side === 'buy' ? { escrowGold: cmd.quantity * cmd.unitPrice } : { escrowUnits: cmd.quantity }),
      };
      marketOrders[cmd.cityId].push(order);
      return { ok: true, createdId: order.id };
    }
    case 'cancel_market_order': {
      for (const cid of CITY_IDS) {
        const i = marketOrders[cid].findIndex((o) => o.id === cmd.orderId);
        if (i >= 0) { marketOrders[cid].splice(i, 1); return { ok: true }; }
      }
      return { ok: false, error: 'No such order.' };
    }
    case 'set_market_tariff': {
      if (!cityDef(cmd.cityId)) return { ok: false, error: 'No such city.' };
      const holderHouse = CHAR_DEFS.find((c) => c.id === cityDef(cmd.cityId)!.holder)?.house;
      if (holderHouse !== PLAYER_HOUSE) return { ok: false, error: 'Only the city holder may set the tariff.' };
      if (cmd.rate < 0 || cmd.rate > MAX_TARIFF_RATE) return { ok: false, error: 'Tariff must be between 0 and 0.15.' };
      cityTariffs[cmd.cityId] = cmd.rate;
      return { ok: true };
    }
    case 'cast_elective_vote': {
      if (!cityDef(cmd.titleId)) return { ok: false, error: 'That title has no city election.' };
      myVote = cmd.candidateId;
      return { ok: true };
    }
    case 'found_company': {
      if (!cityDef(cmd.cityId)) return { ok: false, error: 'No such city.' };
      if (cmd.name.trim().length < 2 || cmd.name.trim().length > 40) return { ok: false, error: 'Company name must be 2 to 40 characters.' };
      const co: CompanyView = {
        id: `co${++orderSeq + 10}`, name: cmd.name.trim(), cityId: cmd.cityId, cityName: cityDef(cmd.cityId)!.name,
        ownerId: cmd.actorId, ownerName: titledName(CHAR_DEFS.find((c) => c.id === cmd.actorId)!) ?? '',
        chain: cmd.chain, licenceLevel: 1, managerId: null, managerName: null, wages: COMPANY_WAGES[1], foundedDay: NOW_DAY,
      };
      companiesState.push(co);
      return { ok: true, createdId: co.id };
    }
    case 'upgrade_company_licence': {
      const co = companiesState.find((x) => x.id === cmd.companyId);
      if (!co) return { ok: false, error: 'No such company.' };
      if (co.licenceLevel >= 3) return { ok: false, error: 'The licence is already at the maximum level.' };
      co.licenceLevel += 1;
      co.wages = COMPANY_WAGES[co.licenceLevel];
      return { ok: true };
    }
    case 'assign_company_manager': {
      const co = companiesState.find((x) => x.id === cmd.companyId);
      if (!co) return { ok: false, error: 'No such company.' };
      const m = CHAR_DEFS.find((c) => c.id === cmd.characterId);
      co.managerId = cmd.characterId;
      co.managerName = m ? `${m.first} of House ${houseName(m.house)}` : null;
      return { ok: true };
    }
    case 'loan_to_liege': {
      if (cmd.amount < 10 || cmd.amount > 500) return { ok: false, error: 'Amount must be between 10 and 500.' };
      if (cmd.rate < 0.05 || cmd.rate > 0.5) return { ok: false, error: 'Rate must be between 0.05 and 0.5.' };
      const loan: MockLoan = {
        id: `ln${loansState.length + 1}`, lenderHouseId: PLAYER_HOUSE, lenderName: 'House Varn',
        borrowerHouseId: 'h3', borrowerName: 'House Orlow', principal: cmd.amount, outstanding: cmd.amount,
        annualRate: cmd.rate, issuedDay: NOW_DAY, missed: 0, defaulted: false,
      };
      loansState.push(loan);
      return { ok: true, createdId: loan.id };
    }
    case 'repay_loan': {
      const loan = loansState.find((x) => x.id === cmd.loanId && x.borrowerHouseId === PLAYER_HOUSE);
      if (!loan) return { ok: false, error: 'No such loan of yours to repay.' };
      if (cmd.amount < 1 || cmd.amount > loan.outstanding) return { ok: false, error: 'Amount must be at most the outstanding debt.' };
      loan.outstanding -= cmd.amount;
      if (loan.outstanding <= 0) loansState.splice(loansState.indexOf(loan), 1);
      return { ok: true };
    }
    default: return { ok: true };
  }
}

// ------------------------------------------------------------------ marriage candidates

/** Deterministic eligible-candidate rows for the marry dialog, best odds first. */
function marriageCandidates(params: URLSearchParams): MarriageCandidate[] {
  const self = CHAR_DEFS.find((d) => d.id === params.get('charId'));
  if (!self) return [];
  const dowry = Math.max(0, Number(params.get('dowry') ?? 0) || 0);
  const matrilineal = ['true', '1'].includes(params.get('matrilineal') ?? '');
  const minAge = Number(params.get('minAge') ?? 0) || 0;
  const maxAge = Number(params.get('maxAge') ?? 0) || 0;
  const limit = Math.min(Number(params.get('limit') ?? 50) || 50, 100);
  const ourHouse = HOUSE_DEFS.find((h) => h.id === self.house);
  const rows = CHAR_DEFS
    .filter((d) => d.id !== self.id && d.house !== self.house && !d.spouse && d.age >= 12 && d.sex !== self.sex)
    .map((d, i) => {
      const house = HOUSE_DEFS.find((h) => h.id === d.house);
      const primary = TITLE_DEFS.filter((t) => t.holder === d.id).sort((a, b) => rankVal(b.rank) - rankVal(a.rank))[0] ?? null;
      const opinion = Math.round(hash((house?.seed ?? 1) * 7 + i) * 90 - 25);
      const attrSum = Object.values(attrs((house?.seed ?? 1) + i, d.bias)).reduce((a, v) => a + v, 0);
      const rankDiff = Math.round(((ourHouse?.prestige ?? 0) - (house?.prestige ?? 0)) / 100);
      const breakdown = { opinion, rankDiff, prestige: ourHouse?.prestige ?? 0, dowry: Math.round(dowry * 0.2), attrs: attrSum };
      // Matrilineal: the husband joins the wife's house, so her side gains (+5) and his side loses a son (-5).
      const swing = matrilineal ? (d.sex === 'F' ? 5 : -5) : 0;
      const score = Math.round(opinion + dowry * 0.2 + rankDiff * 3 + attrSum * 0.2 + swing);
      const verdict = score >= 10 ? 'likely' : score > 0 ? 'possible' : 'unlikely';
      return {
        charId: d.id, name: `${d.first} of House ${houseName(d.house)}`, titledName: titledName(d),
        houseId: d.house, houseName: houseName(d.house) ?? '', age: d.age, sex: d.sex,
        rank: primary?.rank ?? null, primaryTitle: primary?.name ?? null, opinionOfUs: opinion,
        score, verdict, breakdown,
      } satisfies MarriageCandidate;
    })
    .filter((r) => r.age >= minAge && (maxAge === 0 || r.age <= maxAge))
    .sort((a, b) => b.score - a.score);
  return rows.slice(0, limit);
}

// ------------------------------------------------------------------ service candidates

const MOCK_KNIGHT_LIMIT: Record<TitleRank, number> = { barony: 2, city: 2, county: 4, duchy: 6, kingdom: 10 };

/** Deterministic lord rows for the knight-service chooser: own house first, then verdict, then road distance. */
function serviceCandidates(params: URLSearchParams): ServiceCandidate[] {
  const self = CHAR_DEFS.find((d) => d.id === params.get('knightId'));
  if (!self || self.age < 16 || !isDubbed(self.id) || self.knightOf || TITLE_DEFS.some((t) => t.holder === self.id)) return [];
  const dist = new Map<Id, number>([[self.loc ?? 't9', 0]]);
  for (let i = 0; i < ROADS.length; i++) {
    for (const r of ROADS) {
      for (const [x, y] of [[r.a, r.b], [r.b, r.a]]) {
        const dx = dist.get(x);
        if (dx !== undefined && dx + r.days < (dist.get(y) ?? Infinity)) dist.set(y, dx + r.days);
      }
    }
  }
  const rows: ServiceCandidate[] = [];
  for (const d of CHAR_DEFS) {
    const held = TITLE_DEFS.filter((t) => t.holder === d.id);
    if (d.id === self.id || held.length === 0) continue;
    const knightsOf = (tid: Id) => CHAR_DEFS.filter((c) => c.knightOf === tid).length;
    const room = (t: TitleDef) => knightsOf(t.id) < MOCK_KNIGHT_LIMIT[t.rank];
    const best = [...held].sort((a, b) => Number(room(b)) - Number(room(a)) || rankVal(b.rank) - rankVal(a.rank))[0];
    const house = HOUSE_DEFS.find((h) => h.id === d.house);
    const ownHouse = d.house === self.house;
    const op = ownHouse ? 40 : Math.round(hash((house?.seed ?? 1) * 5 + d.age) * 70 - 28);
    const knights = knightsOf(best.id);
    const limit = MOCK_KNIGHT_LIMIT[best.rank];
    const full = knights >= limit;
    const score = full ? -100 : op + 11;
    const seat = rankVal(best.rank) >= rankVal('county') ? (best.capital ?? best.id) : best.id;
    const verdict: ServiceCandidate['verdict'] = full ? 'full' : ownHouse ? 'own' : score >= 10 ? 'likely' : score > 0 ? 'possible' : 'unlikely';
    rows.push({
      titleId: best.id, titleName: best.name, rank: best.rank, holderId: d.id,
      holderName: `${d.first} of House ${house?.name ?? ''}`, holderTitledName: titledName(d),
      houseId: d.house, houseName: house?.name ?? null, ownHouse, control: house?.control ?? 'ai',
      locationId: seat, locationName: TITLE_DEFS.find((t) => t.id === seat)?.name ?? null,
      distanceDays: dist.get(seat) ?? null, knights, limit, opinionOfKnight: op, score, verdict, otherTitles: held.length - 1,
    });
  }
  const order = ['own', 'likely', 'possible', 'unlikely', 'full'];
  return rows.sort((a, b) => order.indexOf(a.verdict) - order.indexOf(b.verdict) || (a.distanceDays ?? 99) - (b.distanceDays ?? 99));
}

/** Road days from a place to every reachable place (the same graph the service rows use). */
function roadDistances(from: Id): Map<Id, number> {
  const dist = new Map<Id, number>([[from, 0]]);
  for (let i = 0; i < ROADS.length; i++) {
    for (const r of ROADS) {
      for (const [x, y] of [[r.a, r.b], [r.b, r.a]]) {
        const dx = dist.get(x);
        if (dx !== undefined && dx + r.days < (dist.get(y) ?? Infinity)) dist.set(y, dx + r.days);
      }
    }
  }
  return dist;
}

/** Dubbed knights a squire-age character could be sent to serve: own house first, then verdict, then standing. */
function squireCandidates(params: URLSearchParams): SquireCandidate[] {
  const self = CHAR_DEFS.find((d) => d.id === params.get('squireId'));
  if (!self || self.age < 12 || (self.age > 21 && CHIVALRY[self.id]?.squireXp === undefined) || isDubbed(self.id) || self.knightOf || TITLE_DEFS.some((t) => t.holder === self.id)
    || HOUSE_DEFS.some((h) => h.headId === self.id)) return [];
  const dist = roadDistances(self.loc ?? 't9');
  const rows: SquireCandidate[] = [];
  for (const k of CHAR_DEFS) {
    if (k.id === self.id || !isDubbed(k.id)) continue;
    const house = HOUSE_DEFS.find((h) => h.id === k.house);
    if (!house) continue;
    const held = TITLE_DEFS.filter((t) => t.holder === k.id).sort((a, b) => rankVal(b.rank) - rankVal(a.rank));
    const court = k.knightOf ?? held[0]?.id ?? null;
    const squires = squireIdsOf(k.id).length;
    const capacity = CHIVALRY[k.id]?.capacity ?? 0;
    const own = house.id === self.house;
    const full = squires >= capacity;
    const op = own ? 40 : Math.round(hash(house.seed * 5 + k.age) * 70 - 28);
    const score = full ? -100 : op + 11;
    const verdict: SquireCandidate['verdict'] = full ? 'full' : own ? 'own' : score >= 10 ? 'likely' : score > 0 ? 'possible' : 'unlikely';
    rows.push({
      knightId: k.id, knightName: `${k.sex === 'F' ? 'Dame' : 'Sir'} ${k.first} of House ${house.name}`, houseId: house.id, houseName: house.name,
      control: house.control, courtTitleId: court, courtName: court ? TITLE_DEFS.find((t) => t.id === court)?.name ?? null : null,
      squires, capacity, standing: charView(k).standing, opinion: op, score, verdict, distanceDays: dist.get(k.loc ?? 't9') ?? null,
    });
  }
  const order = ['own', 'likely', 'possible', 'unlikely', 'full'];
  return rows.sort((a, b) => order.indexOf(a.verdict) - order.indexOf(b.verdict) || b.standing - a.standing).slice(0, 300);
}

// ------------------------------------------------------------------ chat

/** Player accounts in the mock realm (acc1 = the logged-in player, House Varn). */
const CHAT_ACCOUNTS: Record<string, string> = { acc1: 'Varn', acc2: 'Kessel', acc3: 'Orlow' };
/** Minimum ms between two of MY_ACCOUNT's messages, per channel kind — same values as the server. */
const CHAT_SLOW_MS: Record<string, number> = { global: 15_000, newcomers: 5_000 };
const CHAT_MAX_CHARS = 500;
/** Subset of the server's word filter; matched as whole words and masked with asterisks. */
const CHAT_FILTER = ['hell', 'shit', 'damn'] as const;

function chatMask(text: string): string {
  let out = text;
  for (const w of CHAT_FILTER) out = out.replace(new RegExp(`\\b${w}\\b`, 'gi'), '*'.repeat(w.length));
  return out;
}

const chatNow = () => Date.now();
let chatSeq = 0;
const chatId = () => `chat-${String(++chatSeq).padStart(6, '0')}`;

function chatMsg(channelId: string, sender: string, raw: string, agoMs: number, deleted = false): ChatMessage {
  return {
    id: chatId(), channelId, senderAccountId: sender, senderName: `${CHAT_ACCOUNTS[sender]} (player)`,
    text: chatMask(raw), sentAtMs: chatNow() - agoMs, deleted,
  };
}

/** Canned channels: the two global ones, the realm channel of the player's county (t20), one DM. */
const CHAT_DM_ID = 'dm:acc1:acc2';
const chatChannelsDefs: ChatChannelSummary[] = [
  { id: 'newcomers', kind: 'newcomers', name: 'Newcomers', lastMessageAtMs: null, unread: 0 },
  { id: 't20', kind: 'realm', name: 'County of Dunmark', lastMessageAtMs: null, unread: 0 },
  { id: CHAT_DM_ID, kind: 'dm', name: 'Kessel', lastMessageAtMs: null, unread: 0 },
  { id: 'global', kind: 'global', name: 'Global', lastMessageAtMs: null, unread: 0 },
];

/** All messages, newest last. One masked (****) and one admin-deleted, as the spec asks. */
let chatMessages: ChatMessage[] = [
  chatMsg('global', 'acc2', 'The autumn levy is ready. Winter will tell.', 3 * 3600_000 + 5),
  chatMsg('global', 'acc1', 'Good hunting at the fen, or so the foresters say.', 2 * 3600_000 + 4),
  chatMsg('global', 'acc3', 'What a hell of a battle at the ford.', 90 * 60_000 + 3),
  chatMsg('global', 'acc2', 'Watch the roads near Greywater, bandits about.', 40 * 60_000 + 2),
  chatMsg('global', 'acc1', '(struck from the record by a moderator)', 30 * 60_000, true),
  chatMsg('newcomers', 'acc1', 'Welcome, travellers. Ask anything here.', 5 * 3600_000 + 1),
  chatMsg('t20', 'acc1', 'The council meets at dusk. Bring your ledgers.', 4 * 3600_000 + 1),
  chatMsg(CHAT_DM_ID, 'acc2', 'A marriage between our houses would suit us both.', 2 * 3600_000 + 1),
  chatMsg('global', 'acc3', 'Trade tariffs are ruinous this year.', 2 * 60_000),
  chatMsg(CHAT_DM_ID, 'acc2', 'Well? Do we have an accord?', 60_000),
];

/** channelId -> lastReadAtMs of MY_ACCOUNT; unread = messages newer than that, capped at 99. */
const chatReadAt: Record<string, number> = { global: 0, newcomers: 0, t20: 0, [CHAT_DM_ID]: 0 };
/** channelId -> sentAtMs of MY_ACCOUNT's last message there (slow mode). */
const chatLastMine: Record<string, number> = {};
/** accountId -> the other member's (DM) perspective on being blocked. */
let chatBlocked: string[] = [];

function chatChannelDef(channelId: string): ChatChannelSummary | undefined {
  return chatChannelsDefs.find((c) => c.id === channelId);
}

function chatChannelsView(): ChatChannelsResponse {
  return {
    channels: chatChannelsDefs.map((c) => {
      const inChannel = chatMessages.filter((m) => m.channelId === c.id);
      const newest = inChannel.length ? inChannel[inChannel.length - 1].sentAtMs : null;
      const unread = inChannel.filter((m) => m.sentAtMs > (chatReadAt[c.id] ?? 0)).length;
      return { ...c, lastMessageAtMs: newest, unread: Math.min(unread, 99) };
    }).sort((a, b) => (b.lastMessageAtMs ?? 0) - (a.lastMessageAtMs ?? 0)),
    mutedUntilMs: null,
    bannedUntilMs: null,
  };
}

function chatMessagesPage(channelId: string, beforeId: string | null, limit = 50): ChatMessagesResponse {
  const older = (a: ChatMessage, b: ChatMessage) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  const inChannel = chatMessages.filter((m) => m.channelId === channelId).sort(older).reverse(); // newest first
  const after = beforeId ? inChannel.findIndex((m) => m.id === beforeId) : -1;
  const slice = beforeId === null ? inChannel : (after >= 0 ? inChannel.slice(after + 1) : []);
  return { messages: slice.slice(0, limit), hasMore: slice.length > limit };
}

function chatSend(channelId: string, rawText: string): { message: ChatMessage } {
  if (!chatChannelDef(channelId)) throw new MockError(404, 'Unknown channel');
  const text = rawText.trim();
  if (!text) throw new MockError(400, 'Message must not be empty');
  if (text.length > CHAT_MAX_CHARS) throw new MockError(400, `Message too long (max ${CHAT_MAX_CHARS} characters)`);
  const now = chatNow();
  const slow = CHAT_SLOW_MS[channelId] ?? 0;
  const last = chatLastMine[channelId];
  if (slow > 0 && last !== undefined) {
    const waitMs = slow - (now - last);
    if (waitMs > 0) throw new MockError(429, `Slow mode: wait ${Math.ceil(waitMs / 1000)}s before sending again.`);
  }
  if (channelId.startsWith('dm:')) {
    const other = channelId.slice(3).split(':').find((a) => a !== MY_ACCOUNT);
    // Approximation of the server's rule (the recipient's block list stops the sender).
    if (other && chatBlocked.includes(other)) throw new MockError(403, 'You cannot message this player.');
  }
  const message = { ...chatMsg(channelId, MY_ACCOUNT, text, 0), sentAtMs: now, senderName: CHAT_ACCOUNTS[MY_ACCOUNT] ?? 'Varn' };
  chatMessages = [...chatMessages, message];
  chatLastMine[channelId] = now;
  return { message };
}

// ------------------------------------------------------------------ router

class MockError extends Error {
  status: number;
  constructor(status: number, message: string) { super(message); this.status = status; }
}
const delay = (ms = 120) => new Promise((r) => setTimeout(r, ms));

/** Apply the founding screen's choices to the mock player house (name, crest, and the head, spouse and first three children). */
function applyDesign(req: FoundHouseRequest): void {
  const house = HOUSE_DEFS.find((h) => h.id === PLAYER_HOUSE)!;
  if (req.houseName) house.name = req.houseName;
  if (req.crest) house.seed = encodeCrest(req.crest);
  const f = req.family;
  if (!f) return;
  const byId = (id: Id) => CHAR_DEFS.find((c) => c.id === id)!;
  const [head, spouse] = [byId('c1'), byId('c2')];
  head.first = f.head.name; head.sex = f.head.sex;
  spouse.first = f.spouse.name; spouse.sex = f.head.sex === 'M' ? 'F' : 'M';
  faceOverride.c1 = faceFromSeed(f.head.faceSeed);
  faceOverride.c2 = faceFromSeed(f.spouse.faceSeed);
  f.children.forEach((ch, i) => {
    const d = byId(['c3', 'c4', 'c5'][i]);
    d.first = ch.name; d.sex = ch.sex;
    faceOverride[d.id] = childFaceFromSeed(ch.faceSeed, faceOverride.c1, faceOverride.c2);
  });
}

export async function mockRequest(method: 'GET' | 'POST' | 'DELETE', fullPath: string, body?: unknown): Promise<unknown> {
  await delay();
  const [path, query = ''] = fullPath.split('?');
  const params = new URLSearchParams(query);
  const fail = async (status: number, message: string): Promise<never> => {
    const { ApiError } = await import('./client');
    throw new ApiError(status, message);
  };
  try {
    if (path === '/api/auth/register' || path === '/api/auth/login') return { token: 'mock-token', accountId: MY_ACCOUNT };
    if (path === '/api/health') return { ok: true, day: NOW_DAY };
    if (path === '/api/heartbeat' || path === '/api/push/register') return { ok: true };
    if (path === '/api/me') {
      const me: MeResponse = {
        accountId: MY_ACCOUNT, email: 'player@example.com', houseId: founded ? PLAYER_HOUSE : null,
        lastHouseEnded: mode === 'ended' && !endedDismissed && !founded ? { houseId: 'h9', reason: 'inactivity', day: NOW_DAY - 40 } : null, premium: false,
      };
      return me;
    }
    if (path === '/api/house/found') {
      founded = true; endedDismissed = true;
      applyDesign(body as FoundHouseRequest);
      return { accountId: MY_ACCOUNT, email: 'player@example.com', houseId: PLAYER_HOUSE, lastHouseEnded: null, premium: false } satisfies MeResponse;
    }
    if (path === '/api/world') return world();
    if (path === '/api/house') return houseView();
    // exact paths must be matched before the generic /:id regexes below
    if (path === '/api/marriage/candidates') return marriageCandidates(params);
    if (path === '/api/service/candidates') return serviceCandidates(params);
    if (path === '/api/squire/candidates') return squireCandidates(params);
    if (path === '/api/cities') return citiesView();
    if (path === '/api/frontier') return { regions: [] };
    if (path === '/api/companies') return { companies: companiesState };
    if (path === '/api/loans') return loansView();
    if (path === '/api/deliveries') return deliveriesView();
    // ------------------------------------------------------------- chat
    if (path === '/api/chat') return chatChannelsView();
    if (path === '/api/chat/read' && method === 'POST') {
      const channelId = (body as { channelId?: string } | undefined)?.channelId;
      if (typeof channelId !== 'string' || !channelId) return fail(400, 'channelId: Required');
      chatReadAt[channelId] = chatNow();
      return { ok: true };
    }
    let cm: RegExpMatchArray | null = path.match(/^\/api\/chat\/channels\/(.+)\/messages$/);
    if (cm) {
      const channelId = decodeURIComponent(cm[1]);
      if (method === 'POST') {
        const text = (body as { text?: string } | undefined)?.text;
        if (typeof text !== 'string') return fail(400, 'text: Required');
        return chatSend(channelId, text);
      }
      return chatMessagesPage(channelId, params.get('before'), Math.min(Number(params.get('limit') ?? 50) || 50, 100));
    }
    if (path === '/api/chat/blocks') {
      if (method === 'POST') {
        const accountId = (body as { accountId?: string } | undefined)?.accountId;
        if (typeof accountId !== 'string' || !accountId) return fail(400, 'accountId: Required');
        if (accountId !== MY_ACCOUNT && !chatBlocked.includes(accountId)) chatBlocked = [...chatBlocked, accountId];
        return { ok: true };
      }
      return { blocked: chatBlocked.map((accountId) => ({ accountId, houseName: CHAT_ACCOUNTS[accountId] ?? accountId })) };
    }
    cm = path.match(/^\/api\/chat\/blocks\/(.+)$/);
    if (cm && method === 'DELETE') {
      const accountId = decodeURIComponent(cm[1]);
      chatBlocked = chatBlocked.filter((a) => a !== accountId);
      return { ok: true };
    }
    if (path === '/api/chat/dm-targets') {
      return {
        targets: Object.entries(CHAT_ACCOUNTS)
          .filter(([accountId]) => accountId !== MY_ACCOUNT && !chatBlocked.includes(accountId))
          .map(([accountId, houseName]) => ({ accountId, houseName } satisfies DmTarget)),
      };
    }
    if (path === '/api/chat/reports' && method === 'POST') {
      const b = body as { kind?: string; reason?: string; messageId?: string; accountId?: string } | undefined;
      if (b?.kind !== 'message' && b?.kind !== 'user') return fail(400, 'kind: must be message or user');
      if (typeof b.reason !== 'string' || !b.reason.trim()) return fail(400, 'reason: Required');
      if (b.kind === 'message' && typeof b.messageId !== 'string') return fail(400, 'messageId is required for message reports');
      return { ok: true };
    }
    let mm: RegExpMatchArray | null = null;
    mm = path.match(/^\/api\/cities\/(.+)\/market$/);
    if (mm) return marketView(mm[1]);
    mm = path.match(/^\/api\/cities\/(.+)\/guild$/);
    if (mm) return guildView(mm[1]);
    // history subpaths must be matched before the generic /:id regexes below
    let m: RegExpMatchArray | null;
    m = path.match(/^\/api\/characters\/(.+)\/history$/);
    if (m) {
      const d = CHAR_DEFS.find((c) => c.id === m![1]);
      return d ? characterHistory(d) : fail(404, 'No such character');
    }
    m = path.match(/^\/api\/titles\/(.+)\/history$/);
    if (m) {
      const d = TITLE_DEFS.find((t) => t.id === m![1]);
      return d ? [titleTenure(d)] : fail(404, 'No such title');
    }
    m = path.match(/^\/api\/houses\/(.+)\/pedigree$/);
    if (m) {
      const d = HOUSE_DEFS.find((h) => h.id === m![1]);
      if (!d) return fail(404, 'No such house');
      const nodes = CHAR_DEFS.filter((c) => c.house === d.id).map(historyNode);
      // a member who married out (Varn-born, married into Kessel): blood-linked to the Varn head,
      // so the origin house's tree keeps her while her houseId points at the destination house.
      if (d.id === 'h1') {
        nodes.push({
          id: 'c21', name: 'Sara of House Kessel', titledName: 'Sara of House Kessel', gender: 'F',
          birthDay: NOW_DAY - 29 * 360, deathDay: null, houseId: 'h2', houseName: 'Kessel',
          fatherId: 'c1', motherId: 'c2', spouseId: 'c10', childIds: [], isHead: false,
        });
      }
      return nodes;
    }
    m = path.match(/^\/api\/characters\/(.+)$/);
    if (m) {
      const d = CHAR_DEFS.find((c) => c.id === m![1]);
      return d ? charView(d) : fail(404, 'No such character');
    }
    m = path.match(/^\/api\/titles\/(.+)$/);
    if (m) return titleView(m[1]);
    m = path.match(/^\/api\/houses\/(.+)$/);
    if (m) {
      const d = HOUSE_DEFS.find((h) => h.id === m![1]);
      if (!d) return fail(404, 'No such house');
      return { ...houseSummary(d), members: CHAR_DEFS.filter((c) => c.house === d.id).map((c) => summary(charView(c))) };
    }
    if (path === '/api/search/characters') return searchCharacters(params);
    if (path === '/api/chronicle') {
      const before = params.get('before');
      const limit = Number(params.get('limit') ?? 50);
      const mine = params.get('mine') === '1';
      return chronicleAll.filter((e) => (!before || e.id < Number(before)) && (!mine || e.houseIds.includes(PLAYER_HOUSE))).slice(0, limit);
    }
    if (path === '/api/commands' && method === 'POST') {
      const command = (body as { command: Command }).command;
      const result = applyMock(command);
      const q: QueuedCommand = { id: `cmd${++cmdSeq}`, status: result.ok ? 'applied' : 'rejected', command, result, createdAtMs: Date.now() };
      commandsById.set(q.id, q);
      return q;
    }
    m = path.match(/^\/api\/commands\/(.+)$/);
    if (m) return commandsById.get(m[1]) ?? fail(404, 'No such command');
    return fail(404, `Mock: unhandled ${method} ${path}`);
  } catch (e) {
    if (e instanceof MockError) return fail(e.status, e.message);
    throw e;
  }
}
